// stencil
import { h, JSX } from "@stencil/core";

// external libraries
import HTMLChTabularGridRowElement from "@genexus/chameleon-controls-library/dist/types/components/tabular-grid/row/tabular-grid-row";

// local
import {
  MessageData,
  ModifiedObjectData,
  SourceType,
  StatusInternal
} from "./types";
import { ObjectState } from "../common/types";
import ICONS from "./constants";
import { renderObjectTypeWithIcon } from "../../common/utilities";
import { formatDate } from "../../../../common/helpers";
import { CSS_SELECTORS as CLASSES } from "./constants";
import { hiChar } from "../../../ww-files/helpers";
import { config } from "../../../../common/config";
import {
  TabularGridColumnSelectorClickedEvent,
  TabularGridColumnSortChangedEvent
} from "@genexus/chameleon-controls-library";

const BUTTON_ACTION_SELECTOR = "button-action-custom-selector";
export const COLUMNS_IDS = {
  action: "action",
  description: "description",
  lastSynchronized: "lastSynchronized",
  localState: "localState",
  modifiedOn: "modifiedOn",
  module: "module",
  name: "name",
  status: "status",
  type: "type"
};

const renderStateCellContent = (
  action: ObjectState,
  componentLocale: any
): JSX.Element[] | null | string => {
  let icon: string;
  let caption: string;
  let iconColor: string;

  if (action === "deleted") {
    icon = ICONS.substract;
    caption = componentLocale.states.deleted;
    iconColor = "--color-icon-error-default";
  } else if (action === "inserted") {
    icon = ICONS.add;
    caption = componentLocale.states.inserted;
    iconColor = "--color-icon-success-default";
  } else if (action === "modified") {
    icon = ICONS.edit;
    caption = componentLocale.states.modified;
    iconColor = "--color-icon-warning-default";
  } else if (action === "conflicted") {
    icon = ICONS.warning;
    caption = componentLocale.states.conflicted;
    iconColor = "--color-icon-warning-default";
  } else if (!action) {
    // proably is "ignored"
    return null;
  }

  return (
    <span class="custom-icon-text-wrapper">
      <ch-image
        type="mask"
        class="icon-s"
        src={icon}
        style={{ backgroundColor: `var(${iconColor})` }}
      ></ch-image>
      {`${caption}`}
    </span>
  );
};

const renderObjectStatus = (
  objectId: string,
  objectsStatusMap: Map<string, StatusInternal>,
  updatePillAsButton: boolean,
  commitSingleObject: (objectId: string) => () => void,
  componentLocale: any
): JSX.Element[] => {
  const status = objectsStatusMap.get(objectId);
  const caption =
    status === "pending" && updatePillAsButton
      ? componentLocale.status.commit
      : componentLocale.status[status];

  if (status === "committing") {
    return <span class="spinner-caption">{caption}</span>;
  }

  const classes = `pill pill--${status}`;
  if (updatePillAsButton && status === "pending") {
    return (
      <button
        class={{
          "button-secondary": true,
          "button-icon-and-text": true,
          "pill": true,
          "pill--update": true,
          [BUTTON_ACTION_SELECTOR]: true
        }}
        onClick={commitSingleObject(objectId)}
      >
        {caption}
      </button>
    );
  } else {
    return <span class={classes}>{caption}</span>;
  }
};

const renderTabularGridRows = (
  type: SourceType,
  pendingObjectsAfterFilter: ModifiedObjectData[],
  ignoredObjectsAfterFilter: ModifiedObjectData[],
  alreadyUpdatedObjectsIdsSet: Set<string>,
  markedObjectsSet: Set<string>,
  objectsMessagesDataMap: Map<string, MessageData[]>,
  updatePillAsButton: boolean,
  commitSingleObject: (objectId: string) => () => void,
  objectsStatusMap: Map<string, StatusInternal>,
  searchValue: string,
  componentLocale: any
): HTMLChTabularGridRowElement[] => {
  let objects: ModifiedObjectData[];
  if (type === "pending") {
    objects = pendingObjectsAfterFilter;
  } else if (type === "ignored") {
    objects = ignoredObjectsAfterFilter;
  }

  searchValue = searchValue.toLowerCase();

  const filteredRows = objects.map(object => {
    const alreadyUpdated = alreadyUpdatedObjectsIdsSet.has(object.id);
    const hideCheckbox = alreadyUpdated || type === "ignored";
    const marked = markedObjectsSet.has(object.id);
    const messages = objectsMessagesDataMap.get(object.id);

    return (
      <ch-tabular-grid-row
        class="tabular-grid-row"
        key={object.id}
        rowid={object.id}
        ref={(el: HTMLElement) => marked && el?.setAttribute("marked", "true")}
      >
        <ch-tabular-grid-cell
          cell-type="rich"
          row-selector="true"
          class={{
            "tabular-grid-cell": true,
            "no-checkbox-custom-selector": hideCheckbox
          }}
          size="max-content"
        >
          <div
          // This div is a WA to prevent the cell display:flex to
          // apply columns when hiChar is applied. hiChar creates
          // a span for every match.
          >
            {hiChar(object.name, searchValue)}
          </div>
          {/* {object.name} */}
        </ch-tabular-grid-cell>
        <ch-tabular-grid-cell class="tabular-grid-cell">
          {renderObjectTypeWithIcon(object.type)}
        </ch-tabular-grid-cell>
        <ch-tabular-grid-cell
          class={{
            "tabular-grid-cell": true,
            [CLASSES.GRID_CELL_DESCRIPTION]: true
          }}
        >
          <div
          // This div is a WA to prevent the cell display:flex to
          // apply columns when hiChar is applied. hiChar creates
          // a span for every match.
          >
            {hiChar(object.description, searchValue)}
          </div>
        </ch-tabular-grid-cell>
        <ch-tabular-grid-cell class="tabular-grid-cell">
          {formatDate(object.modifiedOn)}
        </ch-tabular-grid-cell>
        <ch-tabular-grid-cell class="tabular-grid-cell">
          {object.module}
        </ch-tabular-grid-cell>
        <ch-tabular-grid-cell class="tabular-grid-cell">
          {renderStateCellContent(object.state, componentLocale)}
        </ch-tabular-grid-cell>
        <ch-tabular-grid-cell class="tabular-grid-cell">
          {formatDate(object.lastSynchronized)}
        </ch-tabular-grid-cell>
        <ch-tabular-grid-cell class="tabular-grid-cell tabular-grid-cell--for-pill">
          {renderObjectStatus(
            object.id,
            objectsStatusMap,
            updatePillAsButton,
            commitSingleObject,
            componentLocale
          )}
        </ch-tabular-grid-cell>

        {messages?.length && (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            <ch-tabular-grid-rowset-empty
              class={{
                "tabular-grid-rowset-empty": true
              }}
            >
              <ul class="messages-list">
                {messages.map(message => {
                  let MESSAGE_ICON;
                  if (message.type === "error") {
                    MESSAGE_ICON = ICONS.error;
                  } else if (message.type === "warning") {
                    MESSAGE_ICON = ICONS.warning;
                  } else if (message.type === "success") {
                    MESSAGE_ICON = ICONS.success;
                  } else {
                    // info
                    MESSAGE_ICON = ICONS.info;
                  }

                  return (
                    <li class="message-description">
                      <ch-image
                        class="icon-s message-icon"
                        src={MESSAGE_ICON}
                      ></ch-image>
                      {message.text}
                    </li>
                  );
                })}
              </ul>
            </ch-tabular-grid-rowset-empty>
          </ch-tabular-grid-rowset>
        )}
      </ch-tabular-grid-row>
    );
  });

  return filteredRows;
};

export default renderTabularGridRows;

export type GridColumnType =
  | "action"
  | "description"
  | "lastSynchronized"
  | "localState"
  | "modifiedOn"
  | "module"
  | "name"
  | "status"
  | "type";

export type GridColumnSet = Partial<Record<GridColumnType, true>>;

export const renderTabularGridColumns = (
  ignoredGridColumns: GridColumnType[],
  gridIsEmpty: boolean = false,
  sortTabularGridColumnCallback: (
    e: CustomEvent<TabularGridColumnSortChangedEvent>
  ) => void,
  columnSelectorClickedCallback: (
    e: CustomEvent<TabularGridColumnSelectorClickedEvent>
  ) => void,
  componentLocale: any,
  showRichRowSelector: boolean
): JSX.Element => {
  const colSize = gridIsEmpty
    ? config.tabularGrid.colSize.auto
    : config.tabularGrid.colSize.maxContent;

  return (
    <ch-tabular-grid-columnset class="tabular-grid-column-set">
      {!ignoredGridColumns.includes("name") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.name}
          columnName={componentLocale.objects.name}
          columnType="rich"
          richRowSelector={showRichRowSelector}
          richRowSelectorMode="mark"
          settingable={false}
          sortable={true}
          onColumnSortChanged={sortTabularGridColumnCallback}
          onColumnSelectorClicked={columnSelectorClickedCallback}
          size={colSize}
        />
      )}

      {!ignoredGridColumns.includes("type") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.type}
          columnName={componentLocale.objects.type}
          settingable={false}
          sortable={true}
          onColumnSortChanged={sortTabularGridColumnCallback}
          size={colSize}
        />
      )}

      {!ignoredGridColumns.includes("description") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.description}
          columnName={componentLocale.objects.description}
          settingable={false}
          sortable={true}
          onColumnSortChanged={sortTabularGridColumnCallback}
          size={config.tabularGrid.colSize.description}
        />
      )}

      {!ignoredGridColumns.includes("modifiedOn") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.modifiedOn}
          columnName={componentLocale.objects.modifiedOn}
          sortable={true}
          onColumnSortChanged={sortTabularGridColumnCallback}
          settingable={false}
          size={colSize}
        />
      )}

      {!ignoredGridColumns.includes("module") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.module}
          columnName={componentLocale.objects.module}
          sortable={true}
          onColumnSortChanged={sortTabularGridColumnCallback}
          settingable={false}
          size={colSize}
        />
      )}

      {!ignoredGridColumns.includes("localState") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.localState}
          columnName={componentLocale.objects.localState}
          sortable={false}
          settingable={false}
          size={colSize}
        />
      )}

      {!ignoredGridColumns.includes("lastSynchronized") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.lastSynchronized}
          columnName={componentLocale.objects.lastSynchronized}
          settingable={false}
          sortable={true}
          onColumnSortChanged={sortTabularGridColumnCallback}
          size={colSize}
        />
      )}

      {!ignoredGridColumns.includes("action") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.action}
          columnName={componentLocale.objects.action}
          sortable={false}
          settingable={false}
          size={colSize}
        />
      )}

      {!ignoredGridColumns.includes("status") && (
        <ch-tabular-grid-column
          class="tabular-grid-column"
          columnId={COLUMNS_IDS.status}
          columnName={componentLocale.objects.status}
          sortable={false}
          settingable={false}
          size={gridIsEmpty ? "auto" : "100px"}
        />
      )}
    </ch-tabular-grid-columnset>
  );
};
